import { useCallback, useEffect, useState } from "react";
import Loading from "../../../../components/Loading";
import MainPageContainer from "../../../../components/MainPageContainer";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import { formatDateForAPI } from "../../../Rajasthan/Utility/const";
import { getEntityComparisonV2 } from "../../../../Rest_api/RestApi";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import LegendSummaryTable, {
  ChartTable,
} from "../../../../components/table/SummaryTable";
import { renderHourTick } from "../../../../components/recharts/components";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { SearchBox } from "../../../../components/Search";
import FilterMenu from "../../../../components/FilterMenu";
import { EntityData } from "../../Exchange3/Data/Entity_data";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";

interface EntityAnalysisDataItem {
  x_axis: number;
  exchange__name: string;
  product__name: string;
  buy_mw: number;
  buy_mwh: number;
  sell_mw: number;
  sell_mwh: number;
  price: number;
}

const DataTabs = ["comparative", "cumulative"];
const exchanges = ["IEX", "PXIL", "HPX"];
const products = ["DAM", "RTM", "GDAM", "HPDAM"];
const UNIT_OPTIONS = ["MW", "MWh"];

const chartSuffixes = [
  {
    suffix: "buy",
    displayName: "Buy Volume",
    stackSuffix: "buy",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "sell",
    displayName: "Sell Volume",
    stackSuffix: "sell",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "price",
    displayName: "Weighted Price",
    stackSuffix: "price",
    chartType: ChartType.Bar,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
];

const EntityAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(["IEX"]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>(["DAM"]);
  const [activeFunctionTab, setActiveFunctionTab] = useState(DataTabs[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNIT_OPTIONS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [rawData, setRawData] = useState<EntityAnalysisDataItem[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<any[]>([
    {
      label: "RAJASTHAN",
      value: 3034,
    },
  ]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? "all"
          : selectedExchanges.join(",");

      const productName =
        selectedProducts.length === products.length
          ? "all"
          : selectedProducts.join(",");

      const newData = await getEntityComparisonV2({
        entityid: selectedEntity[0].value,
        exchangeName: exchangeName,
        productName: productName,
        startDate: startDateStr,
        endDate: endDateStr,
        function: activeFunctionTab as "cumulative" | "comparative",
      });

      setRawData(newData);
      setUniqueBlock(
        Array.from(new Set(newData.map((item) => item.x_axis))).sort(
          (a, b) => a - b
        )
      );
    } catch (error) {
      console.error("Error fetching entity analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedEntity,
    selectedExchanges,
    selectedProducts,
    activeFunctionTab,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    let filteredData = [...rawData];

    if (activeFunctionTab === "comparative") {
      if (
        selectedExchanges.length > 0 &&
        selectedExchanges.length < exchanges.length
      ) {
        filteredData = filteredData.filter((item) =>
          selectedExchanges.includes(item.exchange__name)
        );
      }

      if (
        selectedProducts.length > 0 &&
        selectedProducts.length < products.length
      ) {
        filteredData = filteredData.filter((item) =>
          selectedProducts.includes(item.product__name)
        );
      }
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((block) => block.value).includes(item.x_axis)
      );
    }

    const groupedData: any = {};

    filteredData.forEach((item) => {
      const key = item.x_axis.toString();

      if (!groupedData[key]) {
        groupedData[key] = {
          slot: item.x_axis,
        };
      }

      const exchangeName = item.exchange__name || "";
      const productName = item.product__name || "";
      const prefix =
        activeFunctionTab === "cumulative"
          ? "Cumulative"
          : exchangeName && productName
            ? `${exchangeName}_${productName}`
            : "Cumulative";

      const unitSuffix = selectedUnit.toLowerCase();

      const buyField = `buy_${unitSuffix}` as keyof EntityAnalysisDataItem;
      const sellField = `sell_${unitSuffix}` as keyof EntityAnalysisDataItem;

      const dataKey_buy = `${prefix}_buy_${unitSuffix}`;
      const dataKey_sell = `${prefix}_sell_${unitSuffix}`;
      const dataKey_price = `${prefix}_price`;

      groupedData[key][dataKey_buy] =
        (groupedData[key][dataKey_buy] || 0) + (Number(item[buyField]) || 0);
      groupedData[key][dataKey_sell] =
        (groupedData[key][dataKey_sell] || 0) + (Number(item[sellField]) || 0);
      groupedData[key][dataKey_price] =
        (groupedData[key][dataKey_price] || 0) + (Number(item.price) || 0);
    });

    const formattedChartData = Object.values(groupedData).sort(
      (a: any, b: any) => a.slot - b.slot
    );

    setChartData(formattedChartData);

    const keys =
      activeFunctionTab === "cumulative"
        ? ["Cumulative"]
        : Array.from(
          new Set(
            filteredData.map((item) => {
              const exchangeName = item.exchange__name || "";
              const productName = item.product__name || "";
              return exchangeName && productName
                ? `${exchangeName}_${productName}`
                : "Cumulative";
            })
          )
        );

    setChartKeys(keys);
    setTableData(filteredData);
  }, [
    rawData,
    selectedBlock,
    selectedExchanges,
    selectedProducts,
    activeFunctionTab,
    selectedUnit,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    yAxisId: string,
    unit: string
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }

    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace(/_/g, " ")} ${displayName}`,
      type: chartType,
      yAxisId: yAxisId,
      unit: actualUnit,
      stackId:
        stackSuffix !== undefined && chartType === ChartType.Bar
          ? stackSuffix
          : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      chartSuffixes.forEach((metric) => {
        let actualSuffix = metric.suffix;
        if (metric.unit === "MW/MWh") {
          actualSuffix = `${metric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        legends.push(
          createChartObject(
            key,
            index,
            actualSuffix,
            metric.displayName,
            metric.stackSuffix,
            metric.chartType,
            metric.yAxisId,
            metric.unit
          )
        );
      });
    });

    return legends;
  }

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    tableUnits[curr.suffix] = actualUnit;
  });

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: ["slot"],
    extraLabels: ["Block"],
    filename: AddDatesWithFileName({
      fileName: "Entity_Analysis",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
    customUnits: tableUnits,
    showTotal: false
  };

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">

            <FilterMenu
              title="Exchange"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={(items: string[]) => {
                setSelectedExchanges(items);
              }}
            />

            <FilterMenu
              title="Product"
              items={products}
              selectedItems={selectedProducts}
              setSelectedItems={(items: string[]) => {
                setSelectedProducts(items);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />

            <FilterMenu
              title="Function"
              items={DataTabs.map(
                (tab) => tab.charAt(0).toUpperCase() + tab.slice(1)
              )}
              selectedItems={[
                activeFunctionTab.charAt(0).toUpperCase() +
                activeFunctionTab.slice(1),
              ]}
              onSelect={(item, index) => {
                setActiveFunctionTab(DataTabs[index]);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">Entity Analysis</h2>
              <div className="flex gap-x-6  justify-end">

                <BlockRangeSelector
                  blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                  placeholder="Select Slot"
                  selectedOptions={selectedBlock.map((item) => item.value)}
                  setSelectedOptions={setSelectedBlock}
                />

                <SearchBox
                  options={EntityData.map((item) => ({
                    label: item.name,
                    value: item.id,
                  }))}
                  isMany={false}
                  placeholder="Select Entity"
                  selectedOptions={selectedEntity}
                  onChange={(e: any) => {
                    setSelectedEntity([e]);
                  }}
                />

              </div>
            </div>
            {dataTab.label === "Chart" && (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={false}
                onlyTitle={true}
                secondYAxisLabel="Rs/KWh"
                yAxisLabel={selectedUnit}
                legends={getLegends()}
                xDataKey={"slot"}
                xTick={(value: any, index: any) => {
                  return renderHourTick(value, 10);
                }}
                secondXDataKey={undefined}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...tablePar} />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-2 justify-between gap-5 ">
              <p className="gna-footer-text col-span-1">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default EntityAnalysis;